import { Box, Typography } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import ConformationDialog from "@repo/legacy/components/shared/dialogs/Conformation";
import { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { CreateLoginDialog, type CreateLoginDialogHandle } from "../components/CreateLoginDialog";
import { EditLoginDialog, type EditLoginDialogHandle } from "../components/EditLoginDialog";
import { LoginsDataGrid } from "../components/LoginsDataGrid";
import { LoginsToolbar } from "../components/LoginsToolbar";
import { useLoginsList } from "../hooks/useLoginsList";
import { useResendVerificationEmail } from "../hooks/useResendVerificationEmail";

import type { User } from "@repo/server-api";
import type { ImpersonationState } from "@repo/server-api/impersonation";

export const LoginsPage = () => {
	const { t } = useTranslation();
	const { keycloak } = useKeycloak();

	const {
		rows,
		rowCount,
		isLoading,
		searchValue,
		setSearchValue,
		paginationModel,
		setPaginationModel,
		deleteUser,
		refresh,
	} = useLoginsList();

	const { send: resendVerificationEmail, sendAll: resendAllVerificationEmails } =
		useResendVerificationEmail();

	// Dialog refs
	const editLoginDialogRef = useRef<EditLoginDialogHandle>(null);
	const createLoginDialogRef = useRef<CreateLoginDialogHandle>(null);

	// Delete confirmation state
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState<string | null>(null);

	// Impersonation handler
	const handleImpersonate = useCallback(
		(targetUser: User) => {
			if (!keycloak.authenticated) {
				console.error("Not authenticated");
				return;
			}

			const state: ImpersonationState = {
				targetUserId: targetUser.id,
				targetContactId: targetUser.contactId ?? "",
				adminUsername: keycloak.tokenParsed?.preferred_username || "admin",
				startUrl: window.location.href,
			};

			const newWindow = window.open(window.location.origin, "_blank");

			if (newWindow) {
				const checkInterval = setInterval(() => {
					try {
						if (newWindow.document.readyState === "complete") {
							clearInterval(checkInterval);
							newWindow.postMessage(
								{ type: "START_IMPERSONATION", state },
								window.location.origin,
							);
						}
					} catch {
						// Ignore cross-origin errors during loading
					}
				}, 100);

				setTimeout(() => clearInterval(checkInterval), 5000);
			} else {
				alert(`${t("impersonation-failed")}: ${t("popup-blocked")}`);
			}
		},
		[keycloak, t],
	);

	// Edit user handler
	const handleEditUser = useCallback((targetUser: User) => {
		editLoginDialogRef.current?.show(targetUser);
	}, []);

	// Create new login handler
	const handleCreateNewLogin = useCallback(() => {
		createLoginDialogRef.current?.show();
	}, []);

	// Delete user handlers
	const handleDeleteClick = useCallback((userId: string) => {
		setUserToDelete(userId);
		setDeleteConfirmOpen(true);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		if (userToDelete) {
			deleteUser(userToDelete);
		}
		setDeleteConfirmOpen(false);
		setUserToDelete(null);
	}, [userToDelete, deleteUser]);

	// Resend verification email handler
	const handleResendVerificationEmail = useCallback(
		(targetUser: User) => {
			if (!targetUser.emailVerified) {
				resendVerificationEmail(targetUser.id);
			}
		},
		[resendVerificationEmail],
	);

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h5" sx={{ mb: 2, color: "primary.main" }}>
				{t("all-users-accounts")}
			</Typography>

			<LoginsToolbar
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				onCreateNewLogin={handleCreateNewLogin}
				onResendAllVerificationEmails={resendAllVerificationEmails}
			/>

			<LoginsDataGrid
				rows={rows}
				rowCount={rowCount}
				isLoading={isLoading}
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				onEditUser={handleEditUser}
				onDeleteUser={handleDeleteClick}
				onImpersonateUser={handleImpersonate}
				onResendVerificationEmail={handleResendVerificationEmail}
			/>

			<EditLoginDialog ref={editLoginDialogRef} onSuccess={refresh} />
			<CreateLoginDialog ref={createLoginDialogRef} onSuccess={refresh} />

			<ConformationDialog
				text={t("login.deleteConformation")}
				setOpenDeleteConformation={setDeleteConfirmOpen}
				openPopup={deleteConfirmOpen}
				handleYes={handleDeleteConfirm}
			/>
		</Box>
	);
};
